import React, { useState, useRef } from 'react';
import { DB, uploadImage } from '../data/database';
import { ROLES, CURRENCIES, exportCSV } from '../data/constants';
import { convertCurrency } from '../utils/currency';
import { useLanguage } from '../translations/LanguageContext';
import { DollarSignIcon, CarIcon, EngineIcon, PartsIcon, CheckIcon } from './Icons';
import { logActivity } from '../data/notifications';
import CustomerFilter, { CustomerFilterBanner } from './CustomerFilter';
import InvoiceDetail from './InvoiceDetail';

const STATUS_COLORS = { draft: 'pending', sent: 'transit', viewed: 'transit', partial: 'assigned', paid: 'available', overdue: 'sold', cancelled: 'sold' };
const PAY_METHODS = ['bank_transfer', 'cash', 'western_union', 'crypto', 'cheque', 'other'];
const CSYM = { JPY: '¥', USD: '$', AED: 'AED ', EUR: '€', GBP: '£', GEL: '₾', RUB: '₽', USDC: 'USDC ' };
const currSym = (c) => CSYM[(c || 'AED').toUpperCase()] || ((c || 'AED').toUpperCase() + ' ');
const fmtByCur = (byCur, field) => {
  const keys = Object.keys(byCur);
  if (keys.length === 0) return 'AED 0';
  const active = keys.filter(c => byCur[c][field]);
  if (active.length === 0) return currSym(keys[0]) + '0';
  return active.map(c => currSym(c) + byCur[c][field].toLocaleString()).join(' · ');
};

function Invoices({ user, currency, lang, custFilter, setCustFilter }) {
  const { t } = useLanguage();
  const role = ROLES[user.role] || {};
  const isCustomer = role.isCustomer;
  const [expandedId, setExpandedId] = useState(null);
  const [viewInvoiceId, setViewInvoiceId] = useState(null);
  const [custTab, setCustTab] = useState('action');
  const [uploadInvId, setUploadInvId] = useState(null);
  const [receiptForm, setReceiptForm] = useState({ milestoneIdx: 0, amount: '', method: 'bank_transfer', reference: '', receiptUrl: '' });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const [showCreate, setShowCreate] = useState(false);
  const [, forceUpdate] = useState();

  const allInvoices = DB.get('invoices') || [];
  const allPayments = DB.get('payments') || [];
  const customers = DB.get('customers') || [];
  const containers = DB.get('containers') || [];

  const invoices = isCustomer ? allInvoices.filter(i => i.customerId === user.id) : custFilter ? allInvoices.filter(i => i.customerId === custFilter) : allInvoices;
  const myPayments = isCustomer ? allPayments.filter(p => p.customerId === user.id) : custFilter ? allPayments.filter(p => p.customerId === custFilter) : allPayments;

  // Stats — group by currency, never sum across currencies
  const _missingCur = invoices.filter(i => !i.currency).length;
  if (_missingCur > 0) console.warn(`[Invoices] ${_missingCur} invoice(s) missing currency field — defaulting to AED`);
  const statsByCur = {};
  invoices.forEach(i => {
    const cur = i.currency || 'AED';
    if (!statsByCur[cur]) statsByCur[cur] = { invoiced: 0, received: 0, pending: 0, overdue: 0 };
    statsByCur[cur].invoiced += (i.total || 0);
    statsByCur[cur].received += (i.amountPaid || 0);
    if (['sent', 'partial', 'viewed'].includes(i.status))
      statsByCur[cur].pending += (i.total || 0) - (i.amountPaid || 0);
    if ((Array.isArray(i.paymentMilestones) ? i.paymentMilestones : []).some(m => m.status !== 'paid' && m.due_date && new Date(m.due_date) < new Date()))
      statsByCur[cur].overdue += (i.total || 0) - (i.amountPaid || 0);
  });
  const multiCur = Object.keys(statsByCur).length > 1;

  const actionRequired = invoices.filter(i => ['sent', 'viewed', 'partial', 'overdue'].includes(i.status));
  const completed = invoices.filter(i => i.status === 'paid');
  const proforma = invoices.filter(i => i.invoiceCategory === 'proforma' && i.status === 'draft');
  const other = invoices.filter(i => !actionRequired.includes(i) && !completed.includes(i) && !proforma.includes(i));

  const overdueDays = (d) => { if (!d) return 0; const diff = Math.floor((Date.now() - new Date(d).getTime()) / 86400000); return diff > 0 ? diff : 0; };
  const getIcon = (sku) => !sku ? <DollarSignIcon /> : sku.startsWith('VEH') ? <CarIcon /> : sku.startsWith('ENG') ? <EngineIcon /> : <PartsIcon />;

  // Customer summary cards (admin, no filter) — grouped by currency
  const customerSummaries = !isCustomer && !custFilter ? customers.map(c => {
    const ci = allInvoices.filter(i => i.customerId === c.id);
    const byCur = {};
    ci.forEach(i => {
      const cur = i.currency || 'AED';
      if (!byCur[cur]) byCur[cur] = { invoiced: 0, paid: 0, outstanding: 0, overdue: 0 };
      byCur[cur].invoiced += (i.total || 0);
      byCur[cur].paid += (i.amountPaid || 0);
      byCur[cur].outstanding += (i.total || 0) - (i.amountPaid || 0);
      if ((Array.isArray(i.paymentMilestones) ? i.paymentMilestones : []).some(m => m.status !== 'paid' && m.due_date && new Date(m.due_date) < new Date()))
        byCur[cur].overdue += (i.total || 0) - (i.amountPaid || 0);
    });
    return { ...c, byCur, totalInv: ci.length, pendingInv: ci.filter(i => ['sent', 'partial'].includes(i.status)).length, paidInv: ci.filter(i => i.status === 'paid').length };
  }).filter(c => c.totalInv > 0).sort((a, b) => Object.values(b.byCur).reduce((s, v) => s + v.invoiced, 0) - Object.values(a.byCur).reduce((s, v) => s + v.invoiced, 0)) : [];

  // Upload receipt
  const handleReceiptUpload = async (e) => {
    const file = e.target.files[0]; e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      let url;
      if (file.type.startsWith('image/')) {
        const compressed = await new Promise(resolve => {
          const reader = new FileReader();
          reader.onload = ev => { const img = new Image(); img.onload = () => { const MAX = 1200; let { width: w, height: h } = img; if (w > MAX || h > MAX) { if (w > h) { h = Math.round(h * MAX / w); w = MAX; } else { w = Math.round(w * MAX / h); h = MAX; } } const c = document.createElement('canvas'); c.width = w; c.height = h; c.getContext('2d').drawImage(img, 0, 0, w, h); resolve(c.toDataURL('image/jpeg', 0.75)); }; img.src = ev.target.result; };
          reader.readAsDataURL(file);
        });
        url = await uploadImage(compressed);
      } else {
        const dataUrl = await new Promise(r => { const reader = new FileReader(); reader.onload = e => r(e.target.result); reader.readAsDataURL(file); });
        url = await uploadImage(dataUrl);
      }
      setReceiptForm(prev => ({ ...prev, receiptUrl: url }));
    } catch {}
    setUploading(false);
  };

  const submitReceipt = () => {
    const inv = allInvoices.find(i => i.id === uploadInvId);
    if (!inv || !receiptForm.receiptUrl) return;
    const ms = (Array.isArray(inv.paymentMilestones) ? inv.paymentMilestones : [])[receiptForm.milestoneIdx];

    const payment = {
      id: DB.genId('PAY'), direction: 'incoming', customerId: user.id, partyName: user.name,
      invoiceId: inv.id, amount: Number(receiptForm.amount) || (ms?.amount || 0),
      currency: inv.currency || 'USD', paymentMethod: receiptForm.method,
      reference: receiptForm.reference, status: 'pending_verification',
      receiptUrl: receiptForm.receiptUrl, paymentDate: new Date().toISOString().split('T')[0],
      createdAt: new Date().toISOString(),
    };
    DB.set('payments', [...allPayments, payment]);
    // Notify admin/accountant about payment receipt
    const { Notifications } = require('../data/notifications');
    Notifications.add('payment_received', 'Payment Receipt Uploaded',
      `Payment receipt uploaded by ${user.name} for invoice ${inv.invoiceNumber} — ${currSym(inv?.currency)}${(Number(receiptForm.amount) || ms?.amount || 0).toLocaleString()} — verify`, {});
    setUploadInvId(null);
    setReceiptForm({ milestoneIdx: 0, amount: '', method: 'bank_transfer', reference: '', receiptUrl: '' });
    forceUpdate({});
  };

  // Detail view
  if (viewInvoiceId) {
    if (isCustomer) {
      // Read-only customer detail
      const inv = allInvoices.find(i => i.id === viewInvoiceId);
      if (!inv) return <div><button className="btn btn-secondary btn-sm" onClick={() => setViewInvoiceId(null)}>← Back</button></div>;
      const paidPct = inv.total > 0 ? Math.round((inv.amountPaid || 0) / inv.total * 100) : 0;
      const milestones = Array.isArray(inv.paymentMilestones) ? inv.paymentMilestones : [];
      const invPayments = myPayments.filter(p => p.invoiceId === inv.id);
      return (
        <div>
          <button className="btn btn-secondary btn-sm" onClick={() => setViewInvoiceId(null)} style={{ marginBottom: 16 }}>← Back</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0 }}>{inv.invoiceNumber}</h2>
            <span className={`status status-${STATUS_COLORS[inv.status] || 'pending'}`} style={{ fontSize: 10 }}>{(inv.status || '').replace(/_/g, ' ')}</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>Issued: {inv.issuedDate || '—'} · {(Array.isArray(inv.lineItems) ? inv.lineItems : []).length} items</div>

          {/* Line items read-only */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header"><div className="card-title">Line Items</div></div>
            <div className="card-body" style={{ padding: 0 }}>
              {(Array.isArray(inv.lineItems) ? inv.lineItems : []).map((li, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ color: 'var(--text3)', width: 18 }}>{getIcon(li.unit_sku)}</div>
                  <div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 600 }}>{li.description}</div><div style={{ fontSize: 10, color: 'var(--cyan)', fontFamily: 'monospace' }}>{li.unit_sku}</div></div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--green)' }}>{currSym(inv.currency)}{(li.total || 0).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Total + progress */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-body">
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 800, marginBottom: 8 }}>
                <span>Total</span><span style={{ color: 'var(--green)' }}>{currSym(inv.currency)}{(inv.total || 0).toLocaleString()}</span>
              </div>
              <div style={{ height: 6, background: 'var(--bg3)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${paidPct}%`, background: paidPct >= 100 ? 'var(--green)' : 'var(--blue)', borderRadius: 3, transition: 'width 0.3s' }} />
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Paid: {currSym(inv.currency)}{(inv.amountPaid || 0).toLocaleString()} ({paidPct}%)</div>
            </div>
          </div>

          {/* Milestones */}
          {milestones.length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-header"><div className="card-title">Payment Milestones</div></div>
              <div className="card-body" style={{ padding: 0 }}>
                {milestones.map((m, i) => {
                  const od = overdueDays(m.due_date);
                  return (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderBottom: i < milestones.length - 1 ? '1px solid var(--border)' : 'none' }}>
                      <div><div style={{ fontSize: 13, fontWeight: 600 }}>{m.label}</div><div style={{ fontSize: 10, color: 'var(--text3)' }}>Due: {m.due_date || '—'}{m.paid_date && ` · Paid: ${m.paid_date}`}{m.status !== 'paid' && od > 0 && <span style={{ color: 'var(--red)', marginLeft: 6 }}>{od}d overdue</span>}</div></div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 14, fontWeight: 700 }}>{currSym(inv.currency)}{(m.amount || 0).toLocaleString()}</div>
                        <span style={{ fontSize: 9, fontWeight: 700, color: m.status === 'paid' ? 'var(--green)' : od > 0 ? 'var(--red)' : 'var(--yellow)' }}>{m.status === 'paid' ? '✓ Paid' : od > 0 ? 'Overdue' : 'Pending'}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Payment history for this invoice */}
          {invPayments.length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-header"><div className="card-title">Your Payments</div></div>
              <div className="card-body" style={{ padding: 0 }}>
                {invPayments.map(p => (
                  <div key={p.id} style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>{currSym(p.currency)}{(p.amount || 0).toLocaleString()} · {(p.paymentMethod || '').replace(/_/g, ' ')}</span>
                      <span style={{ color: p.status === 'completed' ? 'var(--green)' : p.status === 'rejected' ? 'var(--red)' : 'var(--yellow)', fontWeight: 600 }}>
                        {p.status === 'completed' ? '✓ Verified' : p.status === 'rejected' ? '✕ Rejected' : '⏳ Pending'}
                      </span>
                    </div>
                    {p.status === 'rejected' && p.notes && <div style={{ color: 'var(--red)', fontSize: 11, marginTop: 4 }}>Reason: {p.notes}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bank Details for customer */}
          {(() => {
            const bankDetails = (() => { try { return DB.get('appConfig')?.bank_details || {}; } catch { return {}; } })();
            if (!bankDetails.bank_name && !bankDetails.iban) return null;
            return (
              <div className="card" style={{ marginBottom: 16 }}>
                <div className="card-header"><div className="card-title" style={{ fontSize: 12 }}>Payment Details</div></div>
                <div className="card-body" style={{ padding: '10px 16px', fontSize: 12 }}>
                  {bankDetails.bank_name && <div><span style={{ color: 'var(--text3)' }}>Bank: </span>{bankDetails.bank_name}</div>}
                  {bankDetails.account_name && <div><span style={{ color: 'var(--text3)' }}>Account: </span>{bankDetails.account_name}</div>}
                  {bankDetails.account_number && <div><span style={{ color: 'var(--text3)' }}>A/C No: </span>{bankDetails.account_number}</div>}
                  {bankDetails.iban && <div><span style={{ color: 'var(--text3)' }}>IBAN: </span>{bankDetails.iban}</div>}
                  {bankDetails.swift_code && <div><span style={{ color: 'var(--text3)' }}>SWIFT: </span>{bankDetails.swift_code}</div>}
                </div>
              </div>
            );
          })()}

          <div style={{ display: 'flex', gap: 10 }}>
            {milestones.some(m => m.status !== 'paid') && <button className="btn btn-primary" onClick={() => { setUploadInvId(inv.id); setReceiptForm(prev => ({ ...prev, amount: milestones.find(m => m.status !== 'paid')?.amount || '', milestoneIdx: milestones.findIndex(m => m.status !== 'paid') })); }}>Upload Receipt</button>}
            <button className="btn btn-secondary" onClick={() => {
              // Simple PDF for customer
              const sym = currSym(inv.currency);
              const rows = (Array.isArray(inv.lineItems) ? inv.lineItems : []).map(li => `<tr><td>${li.unit_sku || '—'}</td><td>${li.description}</td><td style="text-align:right">${sym}${(li.total||0).toLocaleString()}</td></tr>`).join('');
              const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${inv.invoiceNumber}</title><style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:Arial,sans-serif;padding:30px;color:#111;font-size:12px}h1{font-size:20px;margin-bottom:4px}table{width:100%;border-collapse:collapse;margin:16px 0}th{background:#1a3a6b;color:#fff;padding:7px 10px;text-align:left;font-size:10px}td{padding:6px 10px;border-bottom:1px solid #e0e0e0}@media print{body{padding:10px}}</style></head><body><h1>★ BIG STAR</h1><p style="color:#666;font-size:11px;margin-bottom:16px">${inv.invoiceNumber} · ${inv.issuedDate||''}</p><p><strong>${inv.customerName}</strong></p><table><tr><th>SKU</th><th>Description</th><th style="text-align:right">Total</th></tr>${rows}<tr style="font-weight:800;font-size:14px;background:#f0f0f0"><td colspan="2" style="text-align:right;padding:10px">Grand Total</td><td style="text-align:right;padding:10px">${sym}${(inv.total||0).toLocaleString()}</td></tr></table><script>window.onload=()=>{window.print()}<\/script></body></html>`;
              let iframe = document.getElementById('print-frame');
              if (!iframe) { iframe = document.createElement('iframe'); iframe.id='print-frame'; iframe.style.cssText='position:fixed;top:-9999px;left:-9999px;width:0;height:0;border:none;'; document.body.appendChild(iframe); }
              const doc = iframe.contentDocument||iframe.contentWindow.document; doc.open(); doc.write(html); doc.close();
              iframe.onload=()=>{try{iframe.contentWindow.print()}catch{}};
            }}>Download PDF</button>
          </div>
        </div>
      );
    }
    return <InvoiceDetail invoiceId={viewInvoiceId} user={user} onBack={() => setViewInvoiceId(null)} />;
  }

  // Invoice card renderer
  const renderInvoice = (inv) => {
    const paidPct = inv.total > 0 ? Math.round((inv.amountPaid || 0) / inv.total * 100) : 0;
    const overdueMs = (Array.isArray(inv.paymentMilestones) ? inv.paymentMilestones : []).find(m => m.status !== 'paid' && m.due_date && new Date(m.due_date) < new Date());
    const nextMs = (Array.isArray(inv.paymentMilestones) ? inv.paymentMilestones : []).find(m => m.status !== 'paid');
    return (
      <div key={inv.id} className="card" style={{ marginBottom: 10, cursor: 'pointer' }} onClick={() => setViewInvoiceId(inv.id)}>
        <div className="card-body" style={{ padding: '14px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, fontSize: 14 }}>{inv.invoiceNumber}</span>
                <span className={`status status-${STATUS_COLORS[inv.status] || 'pending'}`} style={{ fontSize: 10 }}>{(inv.status || '').replace(/_/g, ' ')}</span>
                {overdueMs && <span style={{ fontSize: 9, background: 'rgba(248,81,73,0.15)', color: 'var(--red)', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>{overdueDays(overdueMs.due_date)}d overdue</span>}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>
                {inv.issuedDate || inv.createdAt}{!isCustomer && inv.customerName && ` · ${inv.customerName}`}{` · ${(Array.isArray(inv.lineItems) ? inv.lineItems : []).length} items`}
              </div>
              {isCustomer && nextMs && nextMs.status !== 'paid' && (
                <div style={{ fontSize: 11, marginTop: 4, color: 'var(--yellow)' }}>
                  Next: {nextMs.label} — {currSym(inv.currency)}{(nextMs.amount || 0).toLocaleString()} due {nextMs.due_date || '—'}
                </div>
              )}
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--green)' }}>{currSym(inv.currency)}{(inv.total || 0).toLocaleString()}</div>
              {(inv.total || 0) - (inv.amountPaid || 0) > 0 && <div style={{ fontSize: 11, color: 'var(--red)' }}>Due: {currSym(inv.currency)}{((inv.total || 0) - (inv.amountPaid || 0)).toLocaleString()}</div>}
            </div>
          </div>
          {inv.total > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text3)', marginBottom: 3 }}><span>{paidPct}% paid</span><span>{currSym(inv.currency)}{(inv.amountPaid || 0).toLocaleString()} / {currSym(inv.currency)}{(inv.total || 0).toLocaleString()}</span></div>
              <div style={{ height: 4, background: 'var(--bg3)', borderRadius: 2, overflow: 'hidden' }}><div style={{ height: '100%', width: `${paidPct}%`, background: paidPct >= 100 ? 'var(--green)' : 'var(--blue)', borderRadius: 2 }} /></div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSection = (icon, title, items) => {
    if (items.length === 0) return null;
    return (<div style={{ marginBottom: 20 }}><div style={{ fontSize: 12, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8, fontWeight: 700 }}>{icon} {title} ({items.length})</div>{items.map(renderInvoice)}</div>);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <h2>{t('invoices')}</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {!isCustomer && (user.role === 'admin' || user.role === 'accountant') && (
            <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>+ Create Invoice</button>
          )}
          {!isCustomer && setCustFilter && <CustomerFilter value={custFilter} onChange={setCustFilter} />}
        </div>
      </div>
      {!isCustomer && setCustFilter && <CustomerFilterBanner value={custFilter} onChange={setCustFilter} />}

      {/* Stats bar */}
      <div className="stats-grid" style={{ marginBottom: 16 }}>
        <div className="stat-card" style={{ padding: 14, textAlign: 'center' }}><div style={{ fontSize: multiCur ? 14 : 20, fontWeight: 800, color: 'var(--blue)' }}>{fmtByCur(statsByCur, 'invoiced')}</div><div style={{ fontSize: 10, color: 'var(--text3)' }}>Invoiced</div></div>
        <div className="stat-card" style={{ padding: 14, textAlign: 'center' }}><div style={{ fontSize: multiCur ? 14 : 20, fontWeight: 800, color: 'var(--green)' }}>{fmtByCur(statsByCur, 'received')}</div><div style={{ fontSize: 10, color: 'var(--text3)' }}>Received</div></div>
        <div className="stat-card" style={{ padding: 14, textAlign: 'center' }}><div style={{ fontSize: multiCur ? 14 : 20, fontWeight: 800, color: 'var(--yellow)' }}>{fmtByCur(statsByCur, 'pending')}</div><div style={{ fontSize: 10, color: 'var(--text3)' }}>Pending</div></div>
        <div className="stat-card" style={{ padding: 14, textAlign: 'center' }}><div style={{ fontSize: multiCur ? 14 : 20, fontWeight: 800, color: Object.values(statsByCur).some(v => v.overdue > 0) ? 'var(--red)' : 'var(--green)' }}>{fmtByCur(statsByCur, 'overdue')}</div><div style={{ fontSize: 10, color: 'var(--text3)' }}>Overdue</div></div>
      </div>

      {/* Customer tabs */}
      {isCustomer && (
        <div className="tabs" style={{ marginBottom: 14 }}>
          <button className={`tab ${custTab === 'action' ? 'active' : ''}`} onClick={() => setCustTab('action')}>Action Required<span className="cnt">{actionRequired.length}</span></button>
          <button className={`tab ${custTab === 'completed' ? 'active' : ''}`} onClick={() => setCustTab('completed')}>Completed<span className="cnt">{completed.length}</span></button>
          <button className={`tab ${custTab === 'history' ? 'active' : ''}`} onClick={() => setCustTab('history')}>Payment History<span className="cnt">{myPayments.length}</span></button>
        </div>
      )}

      {/* Customer: Action Required tab */}
      {isCustomer && custTab === 'action' && (
        actionRequired.length === 0 ? <div style={{ textAlign: 'center', color: 'var(--text3)', padding: 30 }}>No invoices need action.</div> : actionRequired.map(renderInvoice)
      )}

      {/* Customer: Completed tab */}
      {isCustomer && custTab === 'completed' && (
        completed.length === 0 ? <div style={{ textAlign: 'center', color: 'var(--text3)', padding: 30 }}>No completed invoices.</div> : completed.map(renderInvoice)
      )}

      {/* Customer: Payment History tab */}
      {isCustomer && custTab === 'history' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
            <button className="btn btn-secondary btn-sm" onClick={async () => {
              try {
                const { generateInvoicePDF } = await import('../utils/generateInvoicePDF');
                // For now just alert — PDF generation for payment statements can be added later
                alert('Payment statement PDF coming soon. Contact us for a copy.');
              } catch { alert('PDF generation unavailable.'); }
            }}>📄 Export Statement</button>
          </div>
          {myPayments.length === 0 ? <div style={{ textAlign: 'center', color: 'var(--text3)', padding: 30 }}>No payments yet.</div> : (
            [...myPayments].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')).map(p => {
              const inv = allInvoices.find(i => i.id === p.invoiceId);
              return (
                <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'var(--bg3)', borderRadius: 8, marginBottom: 6, fontSize: 12 }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{currSym(p.currency)}{(p.amount || 0).toLocaleString()} · {(p.paymentMethod || '').replace(/_/g, ' ')}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)' }}>{p.paymentDate} · {inv?.invoiceNumber || '—'}{p.reference ? ` · Ref: ${p.reference}` : ''}</div>
                    {p.status === 'rejected' && p.notes && <div style={{ fontSize: 10, color: 'var(--red)', marginTop: 2 }}>Rejected: {p.notes}</div>}
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 700, color: p.status === 'completed' ? 'var(--green)' : p.status === 'rejected' ? 'var(--red)' : 'var(--yellow)' }}>
                    {p.status === 'completed' ? '✓ Verified' : p.status === 'rejected' ? '✕ Rejected' : '⏳ Pending'}
                  </span>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Admin view */}
      {!isCustomer && (
        <>
          {!custFilter && customerSummaries.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 12, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10, fontWeight: 700 }}>Customer Summary</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
                {customerSummaries.map(c => (
                  <div key={c.id} className="card" style={{ cursor: 'pointer' }} onClick={() => setCustFilter(c.id)}>
                    <div className="card-body" style={{ padding: '16px 20px' }}>
                      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{c.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>{c.company}{c.country ? ` · ${c.country}` : ''}</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 12 }}>
                        <div><span style={{ color: 'var(--text3)' }}>Invoiced: </span><strong>{fmtByCur(c.byCur, 'invoiced')}</strong></div>
                        <div><span style={{ color: 'var(--text3)' }}>Paid: </span><strong style={{ color: 'var(--green)' }}>{fmtByCur(c.byCur, 'paid')}</strong></div>
                        <div><span style={{ color: 'var(--text3)' }}>Outstanding: </span><strong style={{ color: Object.values(c.byCur).some(v => v.outstanding > 0) ? 'var(--yellow)' : 'var(--green)' }}>{fmtByCur(c.byCur, 'outstanding')}</strong></div>
                        <div><span style={{ color: 'var(--text3)' }}>Overdue: </span>{Object.values(c.byCur).some(v => v.overdue > 0) ? <strong style={{ color: 'var(--red)' }}>{fmtByCur(c.byCur, 'overdue')}</strong> : <strong style={{ color: 'var(--green)' }}>{currSym(Object.keys(c.byCur)[0])}0</strong>}</div>
                      </div>
                      <div style={{ marginTop: 8, fontSize: 11, color: 'var(--blue)', fontWeight: 600 }}>View Details →</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {renderSection('⚠️', 'Action Required', actionRequired)}
          {renderSection('📋', 'Active', other)}
          {renderSection('📄', 'Proforma', proforma)}
          {renderSection('✅', 'Completed', completed)}
          {invoices.length === 0 && customerSummaries.length === 0 && (
            <div className="empty"><DollarSignIcon /><div className="empty-title">{t('invoice_no_invoices')}</div><div className="empty-text">{t('invoice_empty_msg')}</div></div>
          )}
        </>
      )}

      {/* Upload Receipt Modal */}
      {uploadInvId && (
        <div className="modal-overlay" onClick={() => setUploadInvId(null)}>
          <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3 className="modal-title">Upload Payment Receipt</h3><button className="modal-close" onClick={() => setUploadInvId(null)}>×</button></div>
            <div className="modal-body">
              {(() => {
                const inv = allInvoices.find(i => i.id === uploadInvId);
                const unpaidMs = (inv?.paymentMilestones || []).filter(m => m.status !== 'paid');
                return (
                  <>
                    <div className="form-group">
                      <label className="form-label">Milestone</label>
                      <select className="form-select" value={receiptForm.milestoneIdx} onChange={e => { const idx = Number(e.target.value); setReceiptForm(prev => ({ ...prev, milestoneIdx: idx, amount: (inv?.paymentMilestones || [])[idx]?.amount || '' })); }}>
                        {(inv?.paymentMilestones || []).map((m, i) => m.status !== 'paid' && <option key={i} value={i}>{m.label} — {currSym(inv?.currency)}{(m.amount || 0).toLocaleString()}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Amount Paid</label>
                      <input type="number" className="form-input" value={receiptForm.amount} onChange={e => setReceiptForm(prev => ({ ...prev, amount: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Payment Method</label>
                      <select className="form-select" value={receiptForm.method} onChange={e => setReceiptForm(prev => ({ ...prev, method: e.target.value }))}>
                        {PAY_METHODS.map(m => <option key={m} value={m}>{m.replace(/_/g, ' ')}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Reference Number</label>
                      <input className="form-input" value={receiptForm.reference} onChange={e => setReceiptForm(prev => ({ ...prev, reference: e.target.value }))} placeholder="Transaction reference" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Receipt (image or PDF) <span className="req">*</span></label>
                      {receiptForm.receiptUrl ? (
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                          <img src={receiptForm.receiptUrl} alt="" style={{ width: 100, height: 75, borderRadius: 6, objectFit: 'cover' }} />
                          <button onClick={() => setReceiptForm(prev => ({ ...prev, receiptUrl: '' }))} style={{ position: 'absolute', top: -4, right: -4, width: 18, height: 18, borderRadius: '50%', background: 'var(--red)', border: 'none', color: '#fff', fontSize: 10, cursor: 'pointer' }}>×</button>
                        </div>
                      ) : (
                        <button className="btn btn-secondary btn-sm" onClick={() => fileRef.current?.click()}>📎 Upload Receipt</button>
                      )}
                      <input ref={fileRef} type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={handleReceiptUpload} />
                      {uploading && <div style={{ fontSize: 11, color: 'var(--blue)', marginTop: 4 }}>Uploading...</div>}
                    </div>
                  </>
                );
              })()}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setUploadInvId(null)}>Cancel</button>
              <button className="btn btn-success" disabled={!receiptForm.receiptUrl || uploading} onClick={submitReceipt}><CheckIcon />Submit Receipt</button>
            </div>
          </div>
        </div>
      )}
      {/* ── Create Invoice Modal ── */}
      {showCreate && (
        <CreateInvoiceModal user={user} onClose={() => { setShowCreate(false); forceUpdate({}); }} />
      )}
    </div>
  );
}

// ── Create Invoice Modal ─────────────────────────────────────
function CreateInvoiceModal({ user, onClose }) {
  const { t } = useLanguage();
  const customers = DB.get('customers') || [];
  const containers = DB.get('containers') || [];
  const allUnits = [...(DB.get('vehicles') || []), ...(DB.get('engines') || []), ...(DB.get('spareParts') || [])];

  const [custId, setCustId] = useState('');
  const [category, setCategory] = useState('container');
  const [containerId, setContainerId] = useState('');
  const [lineItems, setLineItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [useUnits, setUseUnits] = useState(false);

  const customer = customers.find(c => c.id === custId);
  const custUnits = allUnits.filter(u => u.assignedTo === custId);
  const custContainers = containers.filter(c => (c.units || []).some(uid => allUnits.find(u => u.id === uid && u.assignedTo === custId)));

  // Fee defaults
  const fees = (() => {
    try {
      const cfg = DB.get('appConfig');
      if (cfg && typeof cfg === 'object' && !Array.isArray(cfg)) return cfg.fee_defaults || {};
    } catch {} return {};
  })();

  const addLine = () => setLineItems(prev => [...prev, { description: '', amount: 0 }]);
  const updateLine = (idx, field, val) => setLineItems(prev => prev.map((li, i) => i === idx ? { ...li, [field]: val } : li));
  const removeLine = (idx) => setLineItems(prev => prev.filter((_, i) => i !== idx));

  const addFromUnits = () => {
    const items = custUnits.map(u => {
      const name = u.make ? `${u.make} ${u.model}` : u.engineName || u.partName || 'Unit';
      const fob = u.pricing?.fob_price_usd || (u.priceJPY ? convertCurrency(u.priceJPY, 'JPY', 'USD') : 0);
      return {
        unit_id: u.id, unit_sku: u.sku,
        description: `${name}${u.year ? ` (${u.year})` : ''}`,
        fob_price: Math.round(fob * 100) / 100,
        amount: Math.round(fob * 100) / 100,
      };
    });
    setLineItems(items);
    setUseUnits(true);
  };

  const total = lineItems.reduce((s, li) => s + (Number(li.amount) || 0), 0);

  const handleSave = async () => {
    if (!custId || lineItems.length === 0) return;
    const invoices = DB.get('invoices') || [];
    const invoiceNumber = await DB.nextInvoiceNumberAsync();

    // Payment milestones
    const terms = customer?.paymentTerms?.milestones || customer?.payment_terms?.milestones || [
      { id: 'full_payment', label: 'Full Payment', percentage: 100 },
    ];
    const paymentMilestones = terms.map((m, i) => {
      const amount = Math.round(total * (m.percentage || 0) / 100 * 100) / 100;
      const dueDate = new Date(); dueDate.setDate(dueDate.getDate() + (i === 0 ? 7 : 30 * (i + 1)));
      return { milestone: m.id || `ms_${i}`, label: m.label, percentage: m.percentage, amount, status: 'pending', due_date: dueDate.toISOString().split('T')[0] };
    });

    const invoice = {
      id: DB.genId('INV'),
      invoiceNumber,
      customerId: custId,
      customerName: customer?.name || '',
      invoiceType: category === 'credit_note' || category === 'debit_note' ? 'adjustment' : 'sale',
      invoiceCategory: category,
      containerId: containerId || '',
      containerNumber: containers.find(c => c.id === containerId)?.number || '',
      lineItems: lineItems.map(li => ({
        ...li,
        fob_price: li.fob_price || li.amount || 0,
        total: Number(li.amount) || 0,
        fees: {},
      })),
      subtotal: total,
      tax: 0,
      total,
      currency: 'USD',
      customerCurrency: customer?.currency || 'USD',
      status: 'draft',
      issuedDate: new Date().toISOString().split('T')[0],
      amountPaid: 0,
      balanceDue: total,
      paymentMilestones,
      notes,
      createdBy: user.id,
      createdAt: new Date().toISOString().split('T')[0],
    };

    DB.set('invoices', [...invoices, invoice]);
    logActivity.invoiceCreated?.(invoiceNumber, customer?.name || '', total, user.name);
    logActivity.invoiceSentToCustomer?.(invoiceNumber, total, paymentMilestones?.[0]?.due_date, custId);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 600 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Create Invoice</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* Customer */}
          <div className="form-group">
            <label className="form-label">Customer <span className="req">*</span></label>
            <select className="form-select" value={custId} onChange={e => { setCustId(e.target.value); setLineItems([]); setUseUnits(false); }}>
              <option value="">Select customer</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ''}</option>)}
            </select>
          </div>

          {/* Category */}
          <div className="form-group">
            <label className="form-label">Invoice Type</label>
            <select className="form-select" value={category} onChange={e => setCategory(e.target.value)}>
              <option value="container">Container Invoice</option>
              <option value="proforma">Proforma Invoice</option>
              <option value="credit_note">Credit Note</option>
              <option value="debit_note">Debit Note</option>
            </select>
          </div>

          {/* Container selector */}
          {category === 'container' && custId && custContainers.length > 0 && (
            <div className="form-group">
              <label className="form-label">Container</label>
              <select className="form-select" value={containerId} onChange={e => setContainerId(e.target.value)}>
                <option value="">Select container</option>
                {custContainers.map(c => <option key={c.id} value={c.id}>{c.number} — {c.destination}</option>)}
              </select>
            </div>
          )}

          {/* Auto-fill from units */}
          {custId && !useUnits && custUnits.length > 0 && (
            <button type="button" className="btn btn-secondary btn-sm" style={{ marginBottom: 12 }} onClick={addFromUnits}>
              Auto-fill from customer's {custUnits.length} unit{custUnits.length !== 1 ? 's' : ''}
            </button>
          )}

          {/* Line items */}
          <div className="form-group">
            <label className="form-label">Line Items</label>
            {lineItems.map((li, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                <input className="form-input" value={li.description} onChange={e => updateLine(i, 'description', e.target.value)} placeholder="Description" style={{ flex: 1, fontSize: 12 }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                  <span style={{ fontSize: 11, color: 'var(--text3)' }}>$</span>
                  <input type="number" className="form-input" value={li.amount || ''} onChange={e => updateLine(i, 'amount', e.target.value)} style={{ width: 80, fontSize: 12, textAlign: 'right' }} />
                </div>
                <button type="button" onClick={() => removeLine(i)} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 16 }}>×</button>
              </div>
            ))}
            <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} onClick={addLine}>+ Add Line Item</button>
          </div>

          {/* Total */}
          {lineItems.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 700, padding: '10px 0', borderTop: '1px solid var(--border)', marginTop: 8 }}>
              <span>Total</span>
              <span style={{ color: category === 'credit_note' ? 'var(--red)' : 'var(--green)' }}>
                {category === 'credit_note' ? '-' : ''}${total.toLocaleString()}
              </span>
            </div>
          )}

          {/* Notes */}
          <div className="form-group" style={{ marginTop: 8 }}>
            <label className="form-label">Notes</label>
            <textarea className="form-input" rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Internal notes..." style={{ resize: 'vertical' }} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>{t('cancel')}</button>
          <button className="btn btn-primary" disabled={!custId || lineItems.length === 0} onClick={handleSave}>
            <CheckIcon /> Save as Draft
          </button>
        </div>
      </div>
    </div>
  );
}

export default Invoices;
